// @flow
import * as React from 'react';
import styled from 'styled-components';
import { GRAD_FAIRS, GRAD_FAIR_NAME_MAP } from 'utils/constants/gradFairs';
import Box from 'components/Box';
import Img from 'components/Image';
import Link from 'components/Link';
import { H4, BodySmall } from 'components/Text';
import { dayNumber, shortMonth, dateIsUpcoming, dateIsToday } from 'utils/date';
import { trackClicked } from 'utils/analytics';
import { colors, fontWeights, transition } from '../../theme';

const Day = styled.div`
  font-weight: ${fontWeights.semiBold};
  font-size: 32px;
  line-height: 1;
`;

const Month = styled.div`
  opacity: 0.8;
  font-size: 16px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const getUrl = url => `${url}?utm_medium=Idealist&utm_source=SearchAd`;

const FairLink = styled(({ active, href, onClick, target, ...props }) =>
  active ? (
    <Box
      component={Link}
      href={href}
      onClick={onClick}
      target={target}
      {...props}
    />
  ) : (
    <Box {...props} />
  )
)`
  ${props =>
    props.active
      ? `
  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15)
      ${props.boxShadow ? `, ${props.boxShadow}` : ''};
    text-decoration: none;
    transition: all ${transition};
    cursor: pointer;
  }`
      : `
    ${Day} {
      text-decoration: line-through;
    }
  `}
`;

type Fair = {
  date: string,
  name: string,
  state: string,
  url: string
};

export const getActiveFairs: () => Array<Fair> = () =>
  GRAD_FAIRS.filter(location => dateIsUpcoming(location.date));

const GradFairsAd = () => {
  const activeFairs = getActiveFairs();
  if (activeFairs.length === 0) return null;
  const fairs = activeFairs.length > 5 ? GRAD_FAIRS : GRAD_FAIRS.slice(5);
  const nextFair = fairs.find(location => dateIsUpcoming(location.date));
  return (
    <Box borderRadius="4px" bg={colors.platinum} p={0} mb={10}>
      <Box
        display={['block', 'flex']}
        p={[20, null, 30]}
        alignItems="center"
        bg="white"
        boxShadow="0 1px 4px rgba(0,0,0,0.15)"
        borderRadius="4px 4px 0 0"
      >
        <Box
          component={Img}
          width={[64, 100]}
          height={[64, 100]}
          flex="0 0 auto"
          src="/static/images/logo-mark.png"
          srcSet="/static/images/logo-mark@2x.png, /static/images/logo-mark@3x.png 2x"
          alt="Idealist logo"
        />
        <Box pl={[0, 30]} mt={[24, 0]}>
          <H4 mb="4px" lineHeight="1.375">
            {getText('Virtual Grad School Fairs 2020')}
          </H4>
          <BodySmall>
            {nextFair && (
              <>
                {`The ${GRAD_FAIR_NAME_MAP[nextFair.id]} Grad Fair is ${
                  dateIsToday(nextFair.date) ? 'today' : 'coming up'
                }!`}
                <br />
              </>
            )}
            {'Click below to learn more:'}
          </BodySmall>
        </Box>
      </Box>
      <Box
        p={30}
        backgroundImage="linear-gradient(to bottom, rgba(0,0,0,0.025) 0%, rgba(0,0,0,0) 5%)"
      >
        <Box display="flex" flexWrap="wrap" m="-3px">
          {fairs.map(location => {
            const active = dateIsUpcoming(location.date);
            const today = dateIsToday(location.date);
            return (
              <Box p="3px" width={[1 / 2, 1 / 3, 1 / 5]} key={location.date}>
                <FairLink
                  active={active}
                  href={active && getUrl(location.url)}
                  onClick={() => {
                    if (active) {
                      trackClicked('Interstitial Grad Fairs Ad', {
                        location: GRAD_FAIR_NAME_MAP[location.id]
                      });
                    }
                  }}
                  target="_blank"
                  display="block"
                  width={1}
                  overflow="hidden"
                  borderRadius={10}
                  color={!active ? colors.battleshipGrey : colors.greyishBrown}
                  boxShadow={
                    active && today && `0 0 0 2px ${colors.dandelion} inset`
                  }
                  bg={!active ? 'rgba(0,0,0,0.05)' : 'white'}
                  textAlign="center"
                >
                  <Box
                    pt="8px"
                    pb="5px"
                    bg={active && today && colors.dandelion}
                  >
                    <Month>{shortMonth(location.date)}</Month>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height={48}
                    px={12}
                    pt="2px"
                    pb={10}
                  >
                    <Day>{dayNumber(location.date)}</Day>
                  </Box>
                </FairLink>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default GradFairsAd;
